/**
 * 点分路径（严格校验错误的 path、引用检查的位置，如 `characters.1.name`）→ 分区、条目名与字段标签。
 * 编辑器用它把错误与引用显示成"条目 · 字段"，并把"去该处"落到对应分区。
 */

export const DEFAULT_SECTION_ID = 'overview'

/** 分区顺序与各分区 JSON 页签编辑的包内顶层键；initial_state 同时挂在概览（起始位置）和物品（持有人与所在地点）。 */
export const SECTION_KEYS = Object.freeze({
  overview: ['title', 'synopsis', 'genres', 'storefront_locales', 'player_id', 'world_rules', 'initial_state'],
  characters: ['characters'],
  locations: ['locations'],
  items: ['items', 'initial_state'],
  facts: ['facts'],
  relations: ['relations'],
  opening: ['opening'],
  narrative: ['narrative', 'scene_templates'],
  drama: [],
})

/** 路径前缀 → 分区；更具体的前缀在前（物品的开场状态落在物品分区，其余开场状态落在概览）。 */
const SECTION_PREFIXES = [
  ['initial_state.items', 'items'],
  ...Object.entries(SECTION_KEYS).flatMap(([section_id, keys]) => keys.map(key => [key, section_id])),
]

export function sectionOfPath(path) {
  if (/^characters\.\d+\.drama_presets(?:\.|$)/.test(path)) return 'drama'
  for (const [prefix, section_id] of SECTION_PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}.`)) return section_id
  }
  return DEFAULT_SECTION_ID
}

/** 校验错误所在分区：没有 path 时按缺失或多余的第一个顶层键定位，仍定位不到归概览。 */
export function issueSection(issue) {
  if (issue.code?.startsWith('drama_')) return 'drama'
  return sectionOfPath(issue.path ?? issue.fields?.missing?.[0] ?? issue.fields?.unexpected?.[0] ?? '')
}

/** 字段模式（数组下标与开场状态里的实体 id 写成 `*`）→ `novelStudio.fields.*` 的标签名。 */
const FIELD_LABELS = Object.freeze({
  schema_version: 'schemaVersion',
  id: 'storyId',
  version: 'version',
  title: 'title',
  synopsis: 'novelAuthoring.synopsis',
  genres: 'novelAuthoring.genres',
  cover_asset_id: 'novelStudio.assets.uploadCover',
  language: 'language',
  player_id: 'playerCharacter',
  world_rules: 'worldRules',
  'initial_state.positions.*': 'startingPositions',
  'initial_state.items.*.holder_id': 'holder',
  'initial_state.items.*.location_id': 'location',
  'characters.*.name': 'name',
  'characters.*.image': 'image',
  'characters.*.public_profile': 'publicProfile',
  'characters.*.profile.identity': 'identity',
  'characters.*.profile.appearance': 'appearance',
  'characters.*.profile.personality': 'personality',
  'characters.*.profile.speech_style': 'speechStyle',
  'characters.*.initial_goal': 'initialGoal',
  'characters.*.performance_states': 'performanceStates',
  'characters.*.performance_states.*.image': 'image',
  'characters.*.performance_states.*.description': 'stateDescription',
  'characters.*.drama_presets': 'dramaPresets',
  'characters.*.drama_presets.default_state_id': 'dramaDefaultState',
  'characters.*.drama_presets.locations': 'dramaLocationRules',
  'characters.*.drama_presets.locations.*.location_id': 'dramaPresetLocation',
  'characters.*.drama_presets.locations.*.state_id': 'dramaPresetState',
  'characters.*.performance_cues': 'performanceCues',
  'characters.*.performance_cues.*.text': 'cueText',
  'characters.*.performance_cues.*.fact_ids': 'factScope',
  'characters.*.performance_cues.*.trigger': 'trigger',
  'characters.*.performance_cues.*.trigger.kind': 'triggerKind',
  'characters.*.performance_cues.*.trigger.entity_id': 'triggerEntity',
  'characters.*.performance_cues.*.trigger.terms': 'triggerTerms',
  'characters.*.performance_cues.*.cooldown_turns': 'cooldown',
  'characters.*.style_examples': 'styleExamples',
  'characters.*.style_examples.*.situation': 'situation',
  'characters.*.style_examples.*.response': 'response',
  'locations.*.name': 'name',
  'locations.*.image': 'image',
  'locations.*.description': 'description',
  'items.*.name': 'name',
  'items.*.image': 'image',
  'items.*.description': 'description',
  'facts.*.text': 'factText',
  'facts.*.observer_ids': 'observers',
  'facts.*.disclosable': 'disclosable',
  'relations.*.from_id': 'from',
  'relations.*.to_id': 'to',
  'relations.*.text': 'relationText',
  'relations.*.observer_ids': 'observers',
  'opening.segments.*.speaker_id': 'speaker',
  'opening.segments.*.channel': 'channel',
  'opening.segments.*.text': 'text',
  'narrative.creative_basis': 'creativeBasis',
  'narrative.initial_time.day': 'storyDay',
  'narrative.initial_time.phase': 'storyPhase',
  'narrative.author_truths': 'authorTruths',
  'scene_templates.*.location_id': 'location',
  'scene_templates.*.present_ids': 'presentCharacters',
  'scene_templates.*.situation': 'situation',
  'scene_templates.*.direction': 'direction',
  'initial_threads.*.kind': 'threadKind',
  'initial_threads.*.participant_ids': 'participants',
  'initial_threads.*.evidence_ids': 'evidenceSegments',
  'initial_threads.*.text': 'threadText',
})

/** 字段标签的 i18n key：路径比已知字段深时逐级截短（`…trigger.terms.2` → 匹配字段）；找不到为 null。 */
function fieldLabelKey(parts) {
  const pattern = parts.map((part, index) => {
    if (/^\d+$/.test(part)) return '*'
    if (index === 2 && parts[0] === 'initial_state') return '*'
    return part
  })
  for (let length = pattern.length; length > 0; length -= 1) {
    const label = FIELD_LABELS[pattern.slice(0, length).join('.')]
    if (label) return label.includes('.') ? label : `novelStudio.fields.${label}`
  }
  return null
}

function nameOf(list, id) {
  const entry = (list ?? []).find(item => item.id === id)
  return entry ? entry.name || entry.id : id
}

/** 路径所指的条目的显示名；不指向条目（顶层字段）为 null。 */
function entryName(pack, parts) {
  const [head, second, third] = parts
  const at = Number(second)
  if (head === 'characters' || head === 'locations' || head === 'items') {
    const entry = pack?.[head]?.[at]
    return entry ? entry.name || entry.id : null
  }
  if (head === 'facts' || head === 'scene_templates' || head === 'initial_threads') return pack?.[head]?.[at]?.id ?? null
  if (head === 'relations') {
    const relation = pack?.relations?.[at]
    return relation ? `${nameOf(pack.characters, relation.from_id)} → ${nameOf(pack.characters, relation.to_id)}` : null
  }
  if (head === 'opening' && second === 'segments' && third !== undefined) return `opening-${third}`
  if (head === 'initial_state' && second === 'positions' && third !== undefined) return nameOf(pack?.characters, third)
  if (head === 'initial_state' && second === 'items' && third !== undefined) return nameOf(pack?.items, third)
  if (head === 'narrative' && second === 'author_truths' && third !== undefined) return `#${Number(third) + 1}`
  return null
}

/**
 * 一个位置的显示信息。
 * @param {Record<string, any>|null} pack 当前工作副本（取条目名）
 * @param {string|null} path
 * @returns {{ section_id: string, entry: string|null, field_key: string|null }}
 */
export function describePath(pack, path) {
  const parts = path ? path.split('.') : []
  return { section_id: sectionOfPath(path ?? ''), entry: entryName(pack, parts), field_key: fieldLabelKey(parts) }
}
